import React from "react";
import classnames from "classnames";
import { Controller, useForm } from "react-hook-form";

import Modal from "components/modal";
import { Textarea } from "components/form-inputs/inputs";
import { ErrorLabel } from "components/form-inputs/shared/error-label";
import { FEEDBACK_DISCLAIMER, FEEDBACK_TEXT_MAX_LENGTH, feedbackTypeOptions, hasFeedbackTypeSelected } from "../lib/constants";

export const AttributeFeedbackModal = ({ attributeName, feedback, isSaving, isReadOnly, onClose, onSubmit }) => {
    const { control, handleSubmit, watch } = useForm({
        mode: "onChange",
        defaultValues: {
            feedbackType: feedback?.feedbackType ?? null,
            feedbackText: feedback?.feedbackText || "",
        },
    });
    const values = watch();
    const feedbackText = values.feedbackText || "";
    const isIncomplete = !hasFeedbackTypeSelected(values.feedbackType) || !feedbackText.trim();
    const isEdit = !!feedback;

    return (
        <Modal
            cssClass="attribute-feedback-modal"
            show={true}
            onClose={onClose}
        >
            <form
                className="flex-column"
                onSubmit={handleSubmit(onSubmit)}
            >
                <h2 className="m-t-0">{isReadOnly ? "Feedback" : isEdit ? "Edit feedback" : "Provide feedback"}</h2>
                <p className="m-t-0">{FEEDBACK_DISCLAIMER}</p>
                <p className="m-t-0">
                    On attribute <strong>{attributeName}</strong>
                </p>
                <div className="form-field">
                    <div className="feedback-label m-b-6">Feedback type</div>
                    <Controller
                        name="feedbackType"
                        control={control}
                        rules={{ validate: (value) => hasFeedbackTypeSelected(value) || "Please select a feedback type." }}
                        render={({ field, fieldState }) => (
                            <ErrorLabel errorMessage={fieldState.error?.message}>
                                <div className="feedback-type-group">
                                    {feedbackTypeOptions.map(({ value, label }) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={classnames("button -small -pill", { "-primary": field.value === value })}
                                            disabled={isReadOnly}
                                            onClick={() => field.onChange(value)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </ErrorLabel>
                        )}
                    />
                </div>
                <div className="form-field">
                    <div className="feedback-label m-b-6">Your feedback</div>
                    <Controller
                        name="feedbackText"
                        control={control}
                        rules={{
                            validate: (value) => !!(value || "").trim() || "Please describe your feedback.",
                            maxLength: { value: FEEDBACK_TEXT_MAX_LENGTH, message: `Feedback cannot exceed ${FEEDBACK_TEXT_MAX_LENGTH} characters.` },
                        }}
                        render={({ field, fieldState }) => (
                            <ErrorLabel errorMessage={fieldState.error?.message}>
                                <Textarea
                                    {...field}
                                    rows={6}
                                    maxLength={FEEDBACK_TEXT_MAX_LENGTH}
                                    readOnly={isReadOnly}
                                    invalid={!!fieldState.error}
                                    placeholder="Describe the issue or suggestion for this attribute..."
                                />
                            </ErrorLabel>
                        )}
                    />
                    <div className="feedback-counter">{`${feedbackText.length}/${FEEDBACK_TEXT_MAX_LENGTH}`}</div>
                </div>
                <div className="border-top button-group -right p-l-12">
                    <button
                        type="button"
                        className="button"
                        onClick={onClose}
                    >
                        {isReadOnly ? "Close" : "Cancel"}
                    </button>
                    {!isReadOnly && (
                        <button
                            type="submit"
                            className={classnames("button -primary", { "-disabled": isIncomplete || isSaving })}
                            disabled={isIncomplete || isSaving}
                        >
                            {isEdit ? "Update feedback" : "Submit feedback"}
                        </button>
                    )}
                </div>
            </form>
        </Modal>
    );
};
